import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  Alert,
  Tooltip as MuiTooltip,
  IconButton,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line
} from 'recharts';
import {
  Timeline,
  Info
} from '@mui/icons-material';
import { get } from '../../services/apiService';

/**
 * MonthlyTestCreationChart component for displaying monthly test creation trends
 * @returns {JSX.Element} MonthlyTestCreationChart component
 */
const MonthlyTestCreationChart = () => {
  const [data, setData] = useState([]);
  const [automationStatuses, setAutomationStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all'); // 'all' for all years, or specific year

  // Simplified automation status mapping (same as MonthlyAutomationStatusChart)
  const mapAutomationStatus = (status) => {
    if (!status) return 'Pending Analysis';
    
    const statusLower = status.toLowerCase();
    
    // Automated group
    if (statusLower.includes('automated') || 
        statusLower.includes('automated in integration framework') ||
        statusLower.includes('automated in new framework') ||
        statusLower.includes('automated in unit framework') ||
        statusLower.includes('automated(old framework)') ||
        statusLower.includes('automated-api') ||
        statusLower.includes('automated-ui')) {
      return 'Automated';
    }
    
    // Manual Test group
    if (statusLower.includes('manual test') || 
        statusLower.includes('manual')) {
      return 'Manual Test';
    }
    
    // Pending Analysis group
    if (statusLower.includes('not automated') || 
        statusLower.includes('tbd')) {
      return 'Pending Analysis';
    }
    
    // To be Automated group
    if (statusLower.includes('ready for epam automation') || 
        statusLower.includes('requires automation')) {
      return 'To be Automated';
    }
    
    // Default fallback
    return 'Pending Analysis';
  };

  // Color palette for simplified automation statuses (same as MonthlyAutomationStatusChart)
  const COLORS = {
    'Automated': '#4caf50',           // Green - Fully automated
    'Manual Test': '#ff9800',         // Orange - Manual tests
    'Pending Analysis': '#9e9e9e',    // Gray - Pending analysis
    'To be Automated': '#2196f3'      // Blue - Ready for automation
  };

  // Fetch monthly test creation data on component mount and when year changes
  useEffect(() => {
    const fetchMonthlyData = async () => {
      try {
        setLoading(true);
        
        // Build API URL with year parameter if not 'all' (using public endpoint)
        const apiUrl = selectedYear === 'all' 
          ? '/api/test/public/monthly-creation'
          : `/api/test/public/monthly-creation?year=${selectedYear}`;
        
        // Call the API endpoint to get monthly test creation data
        const response = await get(apiUrl);
        
        if (response.success) {
          // Transform the data to use simplified status groups
          const transformedData = response.data.map(periodData => {
            const simplifiedData = {
              period: periodData.period,
              totalCount: periodData.totalCount
            };
            
            // Group the detailed statuses into simplified categories
            const statusGroups = {
              'Automated': 0,
              'Manual Test': 0,
              'Pending Analysis': 0,
              'To be Automated': 0
            };
            
            // Map each detailed status to its simplified group
            response.automationStatuses.forEach(status => {
              const simplifiedStatus = mapAutomationStatus(status);
              statusGroups[simplifiedStatus] += periodData[status] || 0;
            });
            
            // Add the grouped counts to the data
            Object.keys(statusGroups).forEach(group => {
              simplifiedData[group] = statusGroups[group];
            });
            
            return simplifiedData;
          });
          
          setData(transformedData);
          setAutomationStatuses(['Automated', 'Manual Test', 'Pending Analysis', 'To be Automated']);
          setError(null);
          console.log('Fetched and transformed monthly test creation data:', transformedData);
        } else {
          throw new Error(response.message || 'Failed to load semi-annual test creation data');
        }
      } catch (err) {
        console.error('Error fetching semi-annual test creation data:', err);
          setError(err.message || 'Failed to load monthly test creation data');
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyData();
  }, [selectedYear]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 2,
            boxShadow: 2
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  mr: 1,
                  borderRadius: 0.5
                }}
              />
              <Typography variant="body2">
                {entry.name}: {entry.value}
              </Typography>
            </Box>
          ))}
          <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
            Total: {payload[0]?.payload?.totalCount || 0}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!data || data.length === 0) return null;
    
    const totalTests = data.reduce((sum, period) => sum + period.totalCount, 0);
    const avgTestsPerPeriod = Math.round(totalTests / data.length);
    const totalPeriods = data.length;
    
    // Calculate automation percentage
    const totalAutomated = data.reduce((sum, period) => sum + (period.Automated || 0), 0);
    const automationPercentage = totalTests > 0 ? Math.round((totalAutomated / totalTests) * 100) : 0;
    
    return {
      totalTests,
      avgTestsPerPeriod,
      totalPeriods,
      automationPercentage
    };
  };

  // Show loading spinner
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading monthly test creation data...
        </Typography>
      </Box>
    );
  }

  // Show error message
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const summaryStats = getSummaryStats();

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Timeline sx={{ mr: 1, color: '#2196f3' }} />
              <Typography variant="h6" component="h3">
                Monthly Test Creation Trends {selectedYear === 'all' ? '(2019 - Now)' : `(${selectedYear})`}
              </Typography>
              <MuiTooltip title="Shows the distribution of test cases created monthly from 2019 to present, grouped by automation status">
                <IconButton size="small">
                  <Info fontSize="small" />
                </IconButton>
              </MuiTooltip>
            </Box>
            
            {/* Year Filter Dropdown */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <MenuItem value="all">All Years</MenuItem>
                {Array.from({ length: new Date().getFullYear() - 2018 }, (_, i) => 2019 + i).map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        }
        subheader={
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Based on {summaryStats?.totalTests} total test cases across {summaryStats?.totalPeriods} periods
            </Typography>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
              Avg: {summaryStats?.avgTestsPerPeriod} tests/period
            </Typography>
            <Chip 
              label={`${summaryStats?.automationPercentage}% Automated`}
              color="success"
              size="small"
            />
          </Box>
        }
      />
      <CardContent>
        <Box height={500}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="period" 
                fontSize={12}
                stroke="#666"
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                label={{ value: 'Number of Tests', angle: -90, position: 'insideLeft' }}
                fontSize={12}
                stroke="#666"
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              
              {/* Stacked bars for each automation status */}
              {automationStatuses.map((status, index) => (
                <Bar 
                  key={status}
                  dataKey={status} 
                  stackId="automation"
                  name={status}
                  fill={COLORS[status] || `hsl(${index * 40}, 70%, 50%)`}
                  radius={index === automationStatuses.length - 1 ? [4, 4, 0, 0] : 0}
                />
              ))}
              
              {/* Line for total trend */}
              <Line
                type="monotone"
                dataKey="totalCount"
                stroke="#ff5722"
                strokeWidth={2}
                name="Total Tests"
                dot={{ fill: '#ff5722', strokeWidth: 2, r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        
        {/* Summary statistics */}
        <Box mt={3} p={2} bgcolor="grey.50" borderRadius={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                  {summaryStats?.totalTests?.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Tests Created
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {summaryStats?.automationPercentage || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Automation Rate
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                  {summaryStats?.avgTestsPerPeriod || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg per Period
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                  {summaryStats?.totalPeriods || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Periods
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MonthlyTestCreationChart;
